import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';

export enum ImageClasses {
  Cached = 'img-cached',
  LoadStarted = 'img-load-started',
  Loaded = 'img-loaded',
}

@Directive({
  selector: '[preloadImage]',
  standalone: false,
})
export class PreloadImageDirective implements OnChanges {
  @Input('preloadImage') imagePath = '';
  @Input() thumb = '';

  private _img?: HTMLImageElement;

  constructor(private renderer: Renderer2, private el: ElementRef) {}

  ngOnChanges(changes: SimpleChanges) {
    const change: SimpleChange = changes.imagePath;
    const shouldChangeUrl = change.currentValue !== change.previousValue;

    if (shouldChangeUrl) {
      if (this.imgIsCached()) {
        this.markCached();
        this.showImage();
      } else {
        this.markLoadStart();
        this.preloadImage();
      }
    }
  }

  imgIsCached() {
    const test: HTMLImageElement = document.createElement('img');
    this.renderer.setAttribute(test, 'src', this.imagePath);
    return test.complete || test.width + test.height > 0;
  }

  markLoadStart() {
    this.renderer.addClass(this.el.nativeElement, ImageClasses.LoadStarted);
    this.renderer.removeClass(this.el.nativeElement, ImageClasses.Loaded);
    this.renderer.removeClass(this.el.nativeElement, ImageClasses.Cached);
  }

  markLoadEnd() {
    requestAnimationFrame(() => {
      this.renderer.addClass(this.el.nativeElement, ImageClasses.Loaded);
      this.renderer.removeClass(this.el.nativeElement, ImageClasses.LoadStarted);
    });
  }

  markCached() {
    this.renderer.addClass(this.el.nativeElement, ImageClasses.Cached);
  }

  showImage() {
    this.setUrl(this.toUrl(this.imagePath));
    this.markLoadEnd();
  }

  preloadImage() {
    this._img = this.renderer.createElement('img');
    this.renderer.setProperty(this._img, 'onload', this.showImage.bind(this));
    this.renderer.setAttribute(this._img, 'src', this.imagePath);
  }

  setUrl(url: string) {
    if (this.isImage) this.renderer.setAttribute(this.el.nativeElement, 'src', url);
    else this.renderer.setAttribute(this.el.nativeElement, 'style', `background-image: ${url};`);
  }

  toUrl(resource: string): string {
    return this.isImage ? resource : `url('${resource}')`;
  }

  get isImage(): boolean {
    return this.el.nativeElement.tagName === 'IMG';
  }
}
